import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../UIControls/ConfirmationDialog';

const UnsavedChangesContext = createContext();

export const UnsavedChangesProvider = ({ children }) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [nextPath, setNextPath] = useState('');
    const navigate = useNavigate();
    const [onSave, setOnSave] = useState(null);

    // Original navigation-focused version
    const promptUnsavedChanges = useCallback(
        (navigateTo) => {
            if (hasUnsavedChanges) {
                setNextPath(navigateTo);
                setShowPrompt(true);
            } else {
                navigate(navigateTo);
            }
        },
        [hasUnsavedChanges, navigate],
    );

    // New action-focused version
    const promptBeforeAction = useCallback(
        (action, navigateTo = null) => {
            if (hasUnsavedChanges) {
                setPendingAction(() => action);
                if (navigateTo) setNextPath(navigateTo);
                setShowPrompt(true);
            } else {
                action();
                if (navigateTo) navigate(navigateTo);
            }
        },
        [hasUnsavedChanges, navigate],
    );

    // const handleSave = useCallback(() => {
    //   if (onSaveCallback) {
    //     onSaveCallback();
    //     setHasUnsavedChanges(false);
    //   }
    // }, [onSaveCallback]);

    const executeAndNavigate = useCallback(() => {
        if (pendingAction) pendingAction();
        setPendingAction(null);
        setHasUnsavedChanges(false);
        if (nextPath) navigate(nextPath);
    }, [pendingAction, nextPath, navigate]);

    return (
        <UnsavedChangesContext.Provider
            value={{
                setOnSave,
                hasUnsavedChanges,
                setHasUnsavedChanges,
                promptUnsavedChanges,
                promptBeforeAction,
                onConfirmNavigation: executeAndNavigate,
                onCancelNavigation: () => {
                    setPendingAction(null);
                    setNextPath('');
                    setShowPrompt(false);
                },
                showPrompt,
            }}
        >
            {children}
            <ConfirmationDialog
                open={showPrompt}
                handleClose={() => {
                    setPendingAction(null);
                    setNextPath('');
                    setShowPrompt(false);
                }}
                onConfirm={executeAndNavigate}
                onSave={onSave}
                title="Unsaved Changes"
                message="You have unsaved changes. Are you sure you want to continue?"
            />
        </UnsavedChangesContext.Provider>
    );
};

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);
export { UnsavedChangesContext };
